import { ApiFormattedResponse } from "@models/api_formatted_response"
import {
  LiveChatWidgetInvalidation,
  LiveChatWidgetOverview,
  LiveChatWidgetRelease,
  PublishLiveChatWidgetReleaseInput,
  RollbackLiveChatWidgetReleaseInput
} from "@models/live_chat_widget"
import { mutate, query } from "@utils/graphql"
import invalidateLiveChatWidgetCacheDoc from "__generated__/queries/mutations/invalidateLiveChatWidgetCache"
import publishLiveChatWidgetReleaseDoc from "__generated__/queries/mutations/publishLiveChatWidgetRelease"
import rollbackLiveChatWidgetReleaseDoc from "__generated__/queries/mutations/rollbackLiveChatWidgetRelease"
import getLiveChatWidgetOverviewDoc from "__generated__/queries/queries/getLiveChatWidgetOverview"
import getLiveChatWidgetReleaseDoc from "__generated__/queries/queries/getLiveChatWidgetRelease"
import getLiveChatWidgetReleasesDoc from "__generated__/queries/queries/getLiveChatWidgetReleases"

/**
 * A folder picked with `webkitdirectory` hands us Files whose `name` is only the
 * basename — the folder part lives on `webkitRelativePath`, which the multipart
 * upload never sends. Re-wrapping restores the relative path so the API can key
 * sub-folders correctly (and strip the `dist/` root the browser prepends).
 */
function withRelativePath(file: File): File {
  const relativePath = (file as File & { webkitRelativePath?: string })
    .webkitRelativePath
  if (!relativePath || relativePath === file.name) return file
  return new File([file], relativePath, { type: file.type })
}

export async function getLiveChatWidgetOverview(): Promise<
  ApiFormattedResponse<LiveChatWidgetOverview>
> {
  const response = await query<{
    getLiveChatWidgetOverview?: { data: LiveChatWidgetOverview }
  }>({
    query: getLiveChatWidgetOverviewDoc,
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.getLiveChatWidgetOverview?.data
  }
}

export async function getLiveChatWidgetReleases(): Promise<
  ApiFormattedResponse<LiveChatWidgetRelease[]>
> {
  const response = await query<{
    getLiveChatWidgetReleases?: { data: LiveChatWidgetRelease[] }
  }>({
    query: getLiveChatWidgetReleasesDoc,
    fetchPolicy: "network-only"
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.getLiveChatWidgetReleases?.data ?? []
  }
}

/** Polled after a publish: the API re-reads CloudFront and flips the status. */
export async function getLiveChatWidgetRelease(
  releaseId: string
): Promise<ApiFormattedResponse<LiveChatWidgetRelease>> {
  const response = await query<{
    getLiveChatWidgetRelease?: { data: LiveChatWidgetRelease }
  }>({
    query: getLiveChatWidgetReleaseDoc,
    fetchPolicy: "network-only",
    variables: { releaseId }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.getLiveChatWidgetRelease?.data
  }
}

export async function publishLiveChatWidgetRelease(
  input: PublishLiveChatWidgetReleaseInput
): Promise<ApiFormattedResponse<LiveChatWidgetRelease>> {
  const response = await mutate<{
    publishLiveChatWidgetRelease?: { data: LiveChatWidgetRelease }
  }>({
    mutation: publishLiveChatWidgetReleaseDoc,
    variables: {
      input: {
        files: input.files.map(withRelativePath),
        version: input.version,
        note: input.note
      }
    }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.publishLiveChatWidgetRelease?.data
  }
}

export async function rollbackLiveChatWidgetRelease(
  input: RollbackLiveChatWidgetReleaseInput
): Promise<ApiFormattedResponse<LiveChatWidgetRelease>> {
  const response = await mutate<{
    rollbackLiveChatWidgetRelease?: { data: LiveChatWidgetRelease }
  }>({
    mutation: rollbackLiveChatWidgetReleaseDoc,
    variables: { input }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.rollbackLiveChatWidgetRelease?.data
  }
}

export async function invalidateLiveChatWidgetCache(): Promise<
  ApiFormattedResponse<LiveChatWidgetInvalidation>
> {
  const response = await mutate<{
    invalidateLiveChatWidgetCache?: { data: LiveChatWidgetInvalidation }
  }>({ mutation: invalidateLiveChatWidgetCacheDoc })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.invalidateLiveChatWidgetCache?.data
  }
}
